import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { AsyncPipe, CommonModule, NgClass, NgIf } from '@angular/common';
import {
  mergeMap,
  Observable,
  of,
  tap,
  map,
  combineLatest,
  BehaviorSubject,
  shareReplay,
} from 'rxjs';
import {
  PlanClaim,
  assistanceType,
  LoadingStatus,
} from '@self-fix/data-access';
import {
  GridColumnComponent,
  GridContainerComponent,
  GridRowComponent,
  GridSectionComponent,
} from '@domgen/dgx-fe-components/grid';
import { IconComponent } from '@domgen/dgx-fe-components/icon';
import { ButtonComponent } from '@domgen/dgx-fe-components/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingOverlayComponent } from '@domgen/dgx-fe-components/loading';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'plan-selection',
  standalone: true,
  imports: [
    AsyncPipe,
    GridColumnComponent,
    GridContainerComponent,
    GridRowComponent,
    GridSectionComponent,
    NgClass,
    NgIf,
    CommonModule,
    IconComponent,
    ButtonComponent,
    LoadingOverlayComponent,
  ],
  templateUrl: './plan-selection.component.html',
  styleUrls: ['./plan-selection.component.scss'],
})
export class PlanSelectionComponent implements AfterViewInit {
  @Input() allPlans$: Observable<PlanClaim[]>;
  @Input() activePlan$: Observable<PlanClaim>;
  @Input() selectedAssistance$: Observable<assistanceType>;
  @Input() loadingStatus$: Observable<LoadingStatus>;
  @Output() selectPlan = new EventEmitter<PlanClaim>();
  @Output() selectAssistance = new EventEmitter<assistanceType>();
  @Output() togglePlanSelection = new EventEmitter<void>();
  showOnlyAssistance = false;

  // Local state for selected plan (doesn't trigger API)
  private localSelectedPlan$ = new BehaviorSubject<PlanClaim | null>(null);

  // Local loading state for continue button
  private isProcessingContinue$ = new BehaviorSubject<boolean>(false);

  // Memoized observables to prevent recreation
  private _displayPlans$?: Observable<any[]>;
  private _hasLocalSelection$?: Observable<boolean>;
  private _isContinueLoading$?: Observable<boolean>;

  // Combined observable to determine which plan should show as selected
  get displayPlans$(): Observable<any[]> {
    if (!this._displayPlans$) {
      this._displayPlans$ = combineLatest([
        this.allPlans$ || of([]),
        this.localSelectedPlan$,
      ]).pipe(
        map(([plans, localPlan]: [any[], PlanClaim | null]) => {
          if (!plans) return [];
          return plans.map((plan: any) => ({
            ...plan,
            isActive: localPlan
              ? plan.plan_number === localPlan.plan_number
              : plan.isActive,
          }));
        }),
        shareReplay(1)
      );
    }
    return this._displayPlans$;
  }

  // Check if a plan is locally selected (for showing assistance section)
  get hasLocalSelection$(): Observable<boolean> {
    if (!this._hasLocalSelection$) {
      this._hasLocalSelection$ = this.localSelectedPlan$.pipe(
        map((plan: PlanClaim | null) => !!plan),
        shareReplay(1)
      );
    }
    return this._hasLocalSelection$;
  }

  // Get the continue loading state
  get isContinueLoading$(): Observable<boolean> {
    if (!this._isContinueLoading$) {
      this._isContinueLoading$ = this.isProcessingContinue$.asObservable();
    }
    return this._isContinueLoading$;
  }

  constructor(
    private readonly destroyRef: DestroyRef,
    private route: ActivatedRoute
  ) {
    // Check if there's a plan number in the URL
    const planNumber = this.route.snapshot.queryParamMap.get('planNumber');
    this.showOnlyAssistance = !!planNumber;
  }
  onSelectPlan(plan: PlanClaim) {
    this.localSelectedPlan$.next(plan);

    setTimeout(() => {
      const assistanceElement = document.getElementById('assistance');
      if (assistanceElement) {
        assistanceElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }, 100);
  }

  onContinue() {
    // Set loading state when continue is clicked
    this.isProcessingContinue$.next(true);

    // When user clicks continue, emit the locally selected plan to trigger API calls
    const localPlan = this.localSelectedPlan$.value;
    if (localPlan) {
      this.selectPlan.emit(localPlan);
    }
    this.togglePlanSelection.emit();
  }
  ngAfterViewInit() {
    this.selectedAssistance$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap((assistance: assistanceType) => {
          if (assistance === 'None') {
            // Reset loading state when assistance is reset to None
            this.isProcessingContinue$.next(false);
            return this.activePlan$;
          } else return of(null); // no assistance, check for plan
        }),
        tap((planNumber: PlanClaim) => {
          // Only set showOnlyAssistance if there's no plan number in URL
          // and we have an active plan
          if (
            planNumber &&
            !this.route.snapshot.queryParamMap.get('planNumber')
          ) {
            this.showOnlyAssistance = true;
          }
        })
      )
      .subscribe();
  }
}
